import React, { useMemo, useState } from "react";
import { Card, Select, Table, Tag, Button, message } from "antd";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { getUserInfo } from "../../utils/redux/reducers/User";
import { ApiManager, Locale } from "../../utils";
import { defaultDateRange, buildTransferAccountsUrl, formatMoneyFen } from "../../utils/statistics/statisticsUtils";
import useStatisticsQuery from "./hooks/useStatisticsQuery";
import StatisticsDateFilter from "./components/StatisticsDateFilter";
import StatCardGrid, { buildMoneyCard } from "./components/StatCardGrid";
import TrendChart from "./components/TrendChart";
import PeriodCompare from "./components/PeriodCompare";
import ExportCsvButton from "./components/ExportCsvButton";
import "./statisticsShared.scss";

const api = ApiManager.getInstance();
const i18n = Locale.getInstance();

const TRANSFER_TYPES = ["all", "commission", "manual"];
const STATUS_OPTIONS = ["all", "success", "pending", "failed", "wait_confirm"];

function TransferStatistics({ user }) {
  const [dateRange, setDateRange] = useState(defaultDateRange(7));
  const [transferType, setTransferType] = useState("all");
  const [status, setStatus] = useState("all");
  const [retrying, setRetrying] = useState(false);

  const filters = useMemo(() => {
    const params = {};
    if (transferType !== "all") params.transfer_type = transferType;
    if (status !== "all") params.status = status;
    return params;
  }, [transferType, status]);

  const { loading, data, refresh } = useStatisticsQuery({
    endpoint: api.statistics.transfers,
    token: user?.token,
    dateRange,
    filters,
  });

  const summary = data?.summary || {};
  const comparison = data?.comparison || {};
  const accounts = data?.accounts || [];
  const users = data?.users || [];

  const statCards = [
    { key: "total", title: i18n.t("statisticsTransfers.totalCount"), value: summary.total_count ?? 0 },
    buildMoneyCard("totalAmount", i18n.t("statisticsTransfers.totalAmount"), summary.total_amount_fen),
    { key: "success", title: i18n.t("statisticsTransfers.successCount"), value: summary.success_count ?? 0 },
    buildMoneyCard("successAmount", i18n.t("statisticsTransfers.successAmount"), summary.success_amount_fen),
    { key: "failed", title: i18n.t("statisticsTransfers.failedCount"), value: summary.failed_count ?? 0 },
    { key: "pending", title: i18n.t("statisticsTransfers.pendingCount"), value: summary.pending_count ?? 0 },
    { key: "waitConfirm", title: i18n.t("statisticsTransfers.waitConfirmCount"), value: summary.wait_confirm_count ?? 0 },
  ];

  const compareItems = [
    { key: "total_count", label: i18n.t("statisticsTransfers.totalCount"), data: comparison.total_count },
    { key: "success_amount_fen", label: i18n.t("statisticsTransfers.successAmount"), data: comparison.success_amount_fen, isMoney: true },
    { key: "failed_count", label: i18n.t("statisticsTransfers.failedCount"), data: comparison.failed_count },
  ];

  const handleRetryTransfers = async () => {
    if (!user?.token) return;
    setRetrying(true);
    try {
      const res = await api.statistics.retryTransfers.run({ token: user.token, data: {} });
      if (res.result) {
        const summary = res.data || {};
        message.success(
          i18n.t("statisticsTransfers.retryDone", {
            retried: summary.retried ?? 0,
            skipped: summary.skipped ?? 0,
            failed: summary.failed ?? 0,
          })
        );
        refresh();
      } else {
        message.warning(res.message);
      }
    } catch (error) {
      message.error(error.message);
    } finally {
      setRetrying(false);
    }
  };

  const accountColumns = [
    {
      title: i18n.t("statisticsOverview.transferAccount"),
      dataIndex: "label",
      render: (text, record) => (
        <span>
          {text || record.mch_id}
          {record.is_default ? <Tag color="blue" style={{ marginLeft: 8 }}>{i18n.t("statisticsOverview.defaultAccount")}</Tag> : null}
          {!record.enabled ? <Tag style={{ marginLeft: 8 }}>{i18n.t("statisticsOverview.disabled")}</Tag> : null}
        </span>
      ),
    },
    { title: i18n.t("statisticsOverview.mchId"), dataIndex: "mch_id" },
    { title: i18n.t("statisticsTransfers.usage"), dataIndex: "usage_fen", render: formatMoneyFen },
    { title: i18n.t("statisticsTransfers.transferCount"), dataIndex: "transfer_count" },
    { title: i18n.t("statisticsTransfers.failedCount"), dataIndex: "failed_count" },
    {
      title: i18n.t("statisticsOverview.remainingQuota"),
      dataIndex: "remaining_quota_fen",
      render: (value) => (value == null ? i18n.t("statisticsOverview.unlimited") : formatMoneyFen(value)),
    },
  ];

  const userColumns = [
    {
      title: i18n.t("statisticsTransfers.userName"),
      dataIndex: "nickname",
      render: (text, record) => text || record.openid || "-",
    },
    { title: i18n.t("statisticsTransfers.userMobile"), dataIndex: "mobile", render: (v) => v || "-" },
    { title: i18n.t("statisticsTransfers.transferCount"), dataIndex: "total_count" },
    { title: i18n.t("statisticsTransfers.totalAmount"), dataIndex: "total_amount_fen", render: formatMoneyFen },
    {
      title: i18n.t("statisticsTransfers.sentAmount"),
      dataIndex: "success_amount_fen",
      render: (value, record) => `${formatMoneyFen(value)} (${record.success_count ?? 0})`,
    },
    {
      title: i18n.t("statisticsTransfers.waitingAmount"),
      dataIndex: "wait_confirm_amount_fen",
      render: (value, record) => `${formatMoneyFen(value)} (${record.wait_confirm_count ?? 0})`,
    },
    { title: i18n.t("statisticsTransfers.failedCount"), dataIndex: "failed_count" },
    { title: i18n.t("statisticsTransfers.failedAmount"), dataIndex: "failed_amount_fen", render: formatMoneyFen },
    { title: i18n.t("statisticsTransfers.pendingCount"), dataIndex: "pending_count" },
    { title: i18n.t("statisticsTransfers.pendingAmount"), dataIndex: "pending_amount_fen", render: formatMoneyFen },
  ];

  return (
    <div className="statistics-page">
      <div className="statistics-page__header">
        <h2>{i18n.t("statisticsTransfers.title")}</h2>
        <div>
          <Button loading={retrying} onClick={handleRetryTransfers} style={{ marginRight: 8 }}>
            {i18n.t("statisticsTransfers.retryTransfers")}
          </Button>
          <Link to={buildTransferAccountsUrl()}>
            <Button type="link">{i18n.t("statisticsCommon.viewTransferAccounts")}</Button>
          </Link>
        </div>
      </div>

      <StatisticsDateFilter value={dateRange} onChange={setDateRange} loading={loading} />

      <Card className="statistics-page__section">
        <div style={{ marginBottom: 16 }}>
          <Select
            style={{ width: 160, marginRight: 8 }}
            value={transferType}
            onChange={setTransferType}
            options={TRANSFER_TYPES.map((v) => ({ value: v, label: i18n.t(`statisticsTransfers.types.${v}`) }))}
          />
          <Select
            style={{ width: 160 }}
            value={status}
            onChange={setStatus}
            options={STATUS_OPTIONS.map((v) => ({ value: v, label: i18n.t(`statisticsTransfers.statuses.${v}`) }))}
          />
        </div>
        <StatCardGrid items={statCards} loading={loading} columns={4} />
      </Card>

      <PeriodCompare items={compareItems} />

      <Card title={i18n.t("statisticsCommon.trend")} className="statistics-page__section">
        <TrendChart
          loading={loading}
          data={data?.trend}
          series={[
            { field: "total_count", name: i18n.t("statisticsTransfers.totalCount"), color: "#1677ff" },
            { field: "success_amount_fen", name: i18n.t("statisticsTransfers.successAmount"), color: "#52c41a" },
          ]}
        />
      </Card>

      <Card
        title={i18n.t("statisticsTransfers.users")}
        className="statistics-page__section"
        extra={<ExportCsvButton filename="transfer-users.csv" columns={userColumns.filter((c) => c.dataIndex)} rows={users} />}
      >
        <Table
          rowKey={(record) => record.user_id || record.openid || record.nickname}
          loading={loading}
          columns={userColumns}
          dataSource={users}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          size="middle"
          scroll={{ x: 1200 }}
        />
      </Card>

      <Card
        title={i18n.t("statisticsTransfers.accounts")}
        className="statistics-page__section"
        extra={<ExportCsvButton filename="transfer-accounts.csv" columns={accountColumns.filter((c) => c.dataIndex)} rows={accounts} />}
      >
        <Table rowKey="id" loading={loading} columns={accountColumns} dataSource={accounts} pagination={false} size="middle" />
      </Card>
    </div>
  );
}

export default connect((state) => ({ user: getUserInfo(state) }))(TransferStatistics);
